import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { dashboardService, widgetService, exportImportService, tagService } from '../services';
import Button from '../components/ui/Button';
import {
  DashboardWidgetInstance,
  WidgetTemplate,
  WidgetType,
  QAItem,
  ConvictionCategory,
  QADataContent,
  ConvictionDataContent,
  ImageRepositoryDataContent,
  PDFRepositoryDataContent,
  NotesRepositoryDataContent,
  ScorecardDataContent,
  TradingScoreDataContent,
  ExcelDataContent,
  SharedWidgetInfo,
  WorkflowUsingDashboard,
  DashboardVersion,
  DashboardVersionDetail,
  Tag,
} from '../types';
import {
  GridLayoutConfig,
  GridItemPosition,
  TabGroupConfig,
  getDefaultGridLayout,
  buildGridFromWidgets,
  getNextY,
  DEFAULT_WIDGET_W,
  DEFAULT_WIDGET_H,
  DEFAULT_MIN_W,
  DEFAULT_MIN_H,
  DEFAULT_TAB_GROUP_MIN_W,
  DEFAULT_TAB_GROUP_MIN_H,
  findOverlaps,
} from '../types/gridLayout';
import { isGridLayoutConfig, migrateFlexLayoutToGrid } from '../utils/layoutMigration';
import { WidgetRenderer, widgetRegistry, SHAREABLE_WIDGET_TYPES, SUMMARY_ELIGIBLE_TYPES } from '../components/widgets';
import { isInputWidget, getWidgetIcon } from '../components/widgets/registry';
import { SCORECARD_LAYOUTS, ScorecardLayout } from '../components/widgets/items/input/scorecard/types';
import DashboardGrid from '../components/dashboard/DashboardGrid';
import { useToast } from '../hooks/useToast';
import DeleteConfirmationModal from '../components/common/DeleteConfirmationModal';
import CopyModal from '../components/common/CopyModal';
import TagPicker from '../components/common/TagPicker';
import SetupWizardModal, { SetupWizardResult } from '../components/common/SetupWizardModal';
import ConfirmationModal from '../components/common/ConfirmationModal';
import { getWidgetLabel } from '../components/widgets/registry';
import { catalogMap, CATEGORY_INFO, CATEGORY_ORDER } from '../components/widgets/catalog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faClipboard, faScroll } from '@fortawesome/free-solid-svg-icons';

/** Get the picker category for a widget type from the catalog */
function getWidgetCategory(type: WidgetType): string | null {
  const entry = catalogMap.get(type);
  return entry ? entry.category : null;
}

// Helper to load layout config from saved data, with migration from legacy FlexLayout format
const loadLayoutConfig = (
  rawConfig: Record<string, unknown>,
  validWidgetIds: string[],
): GridLayoutConfig => {
  if (isGridLayoutConfig(rawConfig)) {
    // New grid format — filter out items for missing widgets
    const config = rawConfig as unknown as GridLayoutConfig;
    const validIds = new Set(validWidgetIds);

    // Collect widget IDs that are in tab groups
    const tabGroupWidgetIds = new Set<string>();
    const filteredTabGroups = config.tabGroups
      .map(g => ({
        ...g,
        widgetIds: g.widgetIds.filter(id => validIds.has(id)),
      }))
      .filter(g => g.widgetIds.length > 0);

    filteredTabGroups.forEach(g => g.widgetIds.forEach(id => tabGroupWidgetIds.add(id)));
    const tabGroupIds = new Set(filteredTabGroups.map(g => g.id));

    const filteredLayout = config.layout.filter(
      item => validIds.has(item.i) || tabGroupIds.has(item.i),
    );

    return {
      ...config,
      layout: filteredLayout,
      tabGroups: filteredTabGroups,
    };
  }

  // Legacy FlexLayout JSON — migrate
  return migrateFlexLayoutToGrid(rawConfig, validWidgetIds);
};

/**
 * Enforce widget-type minimum sizes on a loaded layout.
 * Clamps w/h upward if they fall below the definition's minW/minH.
 */
const reconcileLayoutMinSizes = (
  config: GridLayoutConfig,
  widgets: DashboardWidgetInstance[],
): GridLayoutConfig => {
  const widgetTypeMap = new Map<string, WidgetType>();
  for (const w of widgets) {
    const tmplType = w.configOverrides?.type as WidgetType | undefined;
    if (tmplType) widgetTypeMap.set(w.id, tmplType);
  }

  const reconciledLayout = config.layout.map(item => {
    const wType = widgetTypeMap.get(item.i);
    const def = wType ? widgetRegistry.get(wType) : undefined;
    if (!def) return item;

    const minW = def.defaultSize.minW;
    const minH = def.defaultSize.minH;
    return {
      ...item,
      minW: Math.max(item.minW || 0, minW),
      minH: Math.max(item.minH || 0, minH),
      w: Math.max(item.w, minW),
      h: Math.max(item.h, minH),
    };
  });

  return { ...config, layout: reconciledLayout };
};

const DashboardBuilder: React.FC = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [name, setName] = useState('New Dashboard');
  const [description, setDescription] = useState('');
  const [widgets, setWidgets] = useState<DashboardWidgetInstance[]>([]);
  const [availableTemplates, setAvailableTemplates] = useState<WidgetTemplate[]>([]);
  const [gridLayoutConfig, setGridLayoutConfig] = useState<GridLayoutConfig>(getDefaultGridLayout());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dashboardData, setDashboardData] = useState<Record<string, unknown>>({});

  // Versioning state - for dashboards used by workflows with active assignments
  const [activeAssignmentsCount, setActiveAssignmentsCount] = useState(0);
  const [workflowsUsingDashboard, setWorkflowsUsingDashboard] = useState<WorkflowUsingDashboard[]>(
    [],
  );
  const [showVersionModal, setShowVersionModal] = useState(false);

  // Dashboard version state (like workflows)
  const [currentVersion, setCurrentVersion] = useState<number>(1);
  const [versions, setVersions] = useState<DashboardVersion[]>([]);
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<DashboardVersionDetail | null>(null);

  // Widget data is now configured directly in the dashboard builder sidebar

  // Action modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Tag state
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);

  const loadTags = useCallback(async () => {
    try {
      const data = await tagService.list();
      setAllTags(data);
    } catch (err) {
      console.error('Failed to load tags', err);
    }
  }, []);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  // Widget action menu state
  const [widgetMenuId, setWidgetMenuId] = useState<string | null>(null);
  const [widgetMenuPos, setWidgetMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const widgetMenuRef = useRef<HTMLDivElement>(null);

  // Setup wizard for new dashboards
  const [showSetupWizard, setShowSetupWizard] = useState(!dashboardId);

  // Widget config panel state
  const [expandedConfigWidgetId, setExpandedConfigWidgetId] = useState<string | null>(null);

  // Widget renaming state
  const [editingWidgetId, setEditingWidgetId] = useState<string | null>(null);
  const [editingWidgetName, setEditingWidgetName] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFullscreenWidgetPanel, setShowFullscreenWidgetPanel] = useState(false);
  const [toolbarPos, setToolbarPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const toolbarDragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  // Close version dropdown when clicking outside
  const versionDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        versionDropdownRef.current &&
        !versionDropdownRef.current.contains(event.target as Node)
      ) {
        setShowVersionDropdown(false);
      }
    };
    if (showVersionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVersionDropdown]);

  // Close widget action menu on outside click
  useEffect(() => {
    if (!widgetMenuId) return;
    const handler = (e: MouseEvent) => {
      if (widgetMenuRef.current && !widgetMenuRef.current.contains(e.target as Node)) {
        setWidgetMenuId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [widgetMenuId]);

  // ESC to exit fullscreen mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
        setShowFullscreenWidgetPanel(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Draggable toolbar handlers
  const handleToolbarDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    toolbarDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: toolbarPos.x,
      origY: toolbarPos.y,
    };

    const handleMouseMove = (ev: MouseEvent) => {
      if (!toolbarDragRef.current) return;
      const dx = ev.clientX - toolbarDragRef.current.startX;
      const dy = ev.clientY - toolbarDragRef.current.startY;
      setToolbarPos({
        x: toolbarDragRef.current.origX + dx,
        y: toolbarDragRef.current.origY + dy,
      });
    };

    const handleMouseUp = () => {
      toolbarDragRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [toolbarPos]);

  // Load a specific dashboard version to view (read-only)
  const loadVersionSnapshot = async (versionId: string) => {
    if (!dashboardId) return;
    try {
      const versionDetail = await dashboardService.getDashboardVersion(dashboardId, versionId);
      setViewingVersion(versionDetail);
      // Load the snapshot data into the editor (read-only mode)
      if (versionDetail.snapshotData) {
        setName(versionDetail.snapshotData.name);
        setDescription(versionDetail.snapshotData.description || '');
        setWidgets(versionDetail.snapshotData.widgets || []);
        setDashboardData(versionDetail.snapshotData.data || {});
        // Restore layout
        const versionWidgets = versionDetail.snapshotData.widgets || [];
        const versionWidgetIds = versionWidgets.map(w => w.id);
        if (
          versionDetail.snapshotData.layoutConfig &&
          Object.keys(versionDetail.snapshotData.layoutConfig).length > 0
        ) {
          try {
            setGridLayoutConfig(
              loadLayoutConfig(versionDetail.snapshotData.layoutConfig, versionWidgetIds),
            );
          } catch (e) {
            console.error('Failed to restore version layout:', e);
            setGridLayoutConfig(buildGridFromWidgets(versionWidgetIds));
          }
        }
      }
      setShowVersionDropdown(false);
    } catch (err) {
      console.error('Failed to load version snapshot:', err);
      setError('Failed to load version');
    }
  };

  // Exit version viewing mode and reload current dashboard
  const exitVersionView = async () => {
    setViewingVersion(null);
    if (dashboardId && availableTemplates.length > 0) {
      // Reload the current dashboard
      try {
        setLoading(true);
        const dashboard = await dashboardService.getDashboard(dashboardId);
        setName(dashboard.name);
        setDescription(dashboard.description || '');
        setDashboardData(dashboard.data || {});
        setWidgets(dashboard.widgets || []);
        setCurrentVersion(dashboard.currentVersion || 1);

        // Restore layout
        const reloadWidgetIds = (dashboard.widgets || []).map(w => w.id);
        if (
          reloadWidgetIds.length > 0 &&
          dashboard.layoutConfig &&
          Object.keys(dashboard.layoutConfig).length > 0
        ) {
          try {
            setGridLayoutConfig(loadLayoutConfig(dashboard.layoutConfig, reloadWidgetIds));
          } catch (e) {
            console.error('Failed to restore layout:', e);
            setGridLayoutConfig(buildGridFromWidgets(reloadWidgetIds));
          }
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to reload dashboard');
      } finally {
        setLoading(false);
      }
    }
  };

  // Widget picker filter state
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showTagFilter, setShowTagFilter] = useState(false);
  const [sharedWidgets, setSharedWidgets] = useState<SharedWidgetInfo[]>([]);
  const [loadingSharedWidgets, setLoadingSharedWidgets] = useState(false);

  // Group templates by category
  const templatesByCategory = useMemo(() => {
    const grouped: Record<string, WidgetTemplate[]> = {};
    for (const cat of CATEGORY_ORDER) {
      grouped[cat] = [];
    }

    for (const template of availableTemplates) {
      const category = getWidgetCategory(template.type);
      if (category) {
        grouped[category].push(template);
      }
    }

    return grouped;
  }, [availableTemplates]);

  // Detect overlapping widgets in the current layout
  const overlappingItemIds = useMemo(() => {
    const overlaps = findOverlaps(gridLayoutConfig.layout);
    const ids = new Set<string>();
    for (const [a, b] of overlaps) {
      ids.add(a);
      ids.add(b);
    }
    return ids;
  }, [gridLayoutConfig.layout]);

  const hasOverlaps = overlappingItemIds.size > 0;

  // Filter templates based on search and selected category
  const filteredPickerTemplates = useMemo(() => {
    let templates = availableTemplates;

    // Filter by category
    if (selectedCategory && selectedCategory !== 'shared') {
      templates = templatesByCategory[selectedCategory];
    }

    // Apply search filter
    if (pickerSearch) {
      const query = pickerSearch.toLowerCase();
      templates = templates.filter(template => {
        const matchesName = template.name.toLowerCase().includes(query);
        const matchesDesc = template.description?.toLowerCase().includes(query);
        const matchesType = getWidgetLabel(template.type).toLowerCase().includes(query);
        return matchesName || matchesDesc || matchesType;
      });
    }

    // Only show templates that have a category (exclude old widget types)
    return templates.filter(t => getWidgetCategory(t.type));
  }, [availableTemplates, selectedCategory, pickerSearch, templatesByCategory]);

  // Filter shared widgets based on search (exclude current dashboard's widgets)
  const filteredSharedWidgets = useMemo(() => {
    // Exclude widgets from the current dashboard
    let filtered = sharedWidgets.filter(w => w.dashboardId !== dashboardId);

    if (pickerSearch) {
      const query = pickerSearch.toLowerCase();
      filtered = filtered.filter(
        w =>
          w.widgetTitle.toLowerCase().includes(query) ||
          w.dashboardName.toLowerCase().includes(query) ||
          getWidgetLabel(w.widgetType)?.toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [sharedWidgets, dashboardId, pickerSearch]);

  // Load available widget templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await widgetService.getWidgetTemplates(1, 100);
        setAvailableTemplates(response.data);
      } catch (err) {
        console.error('Failed to load widget templates:', err);
      }
    };
    loadTemplates();
  }, []);

  // Load shared widgets when picker opens
  const loadSharedWidgets = useCallback(async () => {
    try {
      setLoadingSharedWidgets(true);
      const response = await dashboardService.getSharedWidgets();
      setSharedWidgets(response.sharedWidgets);
    } catch (err) {
      console.error('Failed to load shared widgets:', err);
    } finally {
      setLoadingSharedWidgets(false);
    }
  }, []);

  // Load existing dashboard
  useEffect(() => {
    if (dashboardId && availableTemplates.length > 0) {
      const loadDashboard = async () => {
        try {
          setLoading(true);
          const dashboard = await dashboardService.getDashboard(dashboardId);
          setName(dashboard.name);
          setDescription(dashboard.description || '');
          setDashboardData(dashboard.data || {});
          setTagIds((dashboard.tags || []).map(t => t.id));

          // Validate widgets - filter out those with missing templates
          const validWidgets: DashboardWidgetInstance[] = [];
          const brokenWidgets: string[] = [];
          const templateIds = new Set(availableTemplates.map(t => t.id));

          for (const widget of dashboard.widgets || []) {
            if (templateIds.has(widget.templateId)) {
              validWidgets.push(widget);
            } else {
              brokenWidgets.push(widget.title || widget.templateId);
            }
          }

          if (brokenWidgets.length > 0) {
            setError(
              `Some widgets reference deleted templates and were removed: ${brokenWidgets.join(', ')}. Save to clean up.`,
            );
          }

          setWidgets(validWidgets);

          // Load active assignments count and version info for versioning prompt
          try {
            const [assignmentsResponse, versionsResponse] = await Promise.all([
              dashboardService.getActiveAssignmentsCount(dashboardId),
              dashboardService.getDashboardVersions(dashboardId),
            ]);
            setActiveAssignmentsCount(assignmentsResponse.activeAssignmentsCount);
            setWorkflowsUsingDashboard(assignmentsResponse.workflowsUsingDashboard);
            setVersions(versionsResponse.versions);
            setCurrentVersion(versionsResponse.currentVersion);
          } catch (e) {
            console.error('Failed to load active assignments/versions:', e);
            // Non-fatal error, continue with loading
          }

          // Restore layout (with migration from legacy FlexLayout format if needed)
          const validWidgetIds = validWidgets.map(w => w.id);
          if (
            validWidgets.length > 0 &&
            dashboard.layoutConfig &&
            Object.keys(dashboard.layoutConfig).length > 0
          ) {
            try {
              setGridLayoutConfig(loadLayoutConfig(dashboard.layoutConfig, validWidgetIds));
            } catch (e) {
              console.error('Failed to restore layout:', e);
              setGridLayoutConfig(buildGridFromWidgets(validWidgetIds));
            }
          } else if (validWidgets.length > 0) {
            // No saved layout but we have widgets - rebuild layout
            setGridLayoutConfig(buildGridFromWidgets(validWidgetIds));
          }
        } catch (err: any) {
          setError(err.response?.data?.detail || 'Failed to load dashboard');
        } finally {
          setLoading(false);
        }
      };
      loadDashboard();
    }
  }, [dashboardId, availableTemplates]);

  // Reconcile layout min sizes when widgets or layout change
  useEffect(() => {
    if (widgets.length === 0 || gridLayoutConfig.layout.length === 0) return;
    // Build widget ID → type map from templates
    const templateTypeMap = new Map(availableTemplates.map(t => [t.id, t.type as WidgetType]));
    const widgetInstances = widgets.map(w => ({
      ...w,
      configOverrides: { ...w.configOverrides, type: templateTypeMap.get(w.templateId) },
    }));
    const reconciled = reconcileLayoutMinSizes(gridLayoutConfig, widgetInstances);
    // Only update if something actually changed
    const changed = reconciled.layout.some((item, i) => {
      const orig = gridLayoutConfig.layout[i];
      return orig && (item.w !== orig.w || item.h !== orig.h || item.minW !== orig.minW || item.minH !== orig.minH);
    });
    if (changed) setGridLayoutConfig(reconciled);
  }, [widgets, availableTemplates]); // Only run on initial load, not on every layout change

  // Callback for widget data changes (Q&A, Conviction, Image Repository, PDF Repository, Notes Repository, etc.)
  const handleDashboardDataChange = useCallback(
    (
      dataKey: string,
      newData:
        | QADataContent
        | ConvictionDataContent
        | ImageRepositoryDataContent
        | PDFRepositoryDataContent
        | NotesRepositoryDataContent
        | ScorecardDataContent
        | TradingScoreDataContent
        | ExcelDataContent,
    ) => {
      setDashboardData(prev => ({
        ...prev,
        [dataKey]: newData,
      }));
    },
    [],
  );

  // --- Widget Configuration Panels (rendered in sidebar) ---

  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Q&A config panel
  const renderQAConfigPanel = (widget: DashboardWidgetInstance) => {
    const dataKey = getWidgetDataKey(widget);
    const qaData = (dashboardData[dataKey] as QADataContent) || { title: '', items: [] };
    const items: QAItem[] = qaData.items || [];

    const updateQA = (updates: Partial<QADataContent>) => {
      handleDashboardDataChange(dataKey, { ...qaData, ...updates } as QADataContent);
    };

    const addQuestion = () => {
      updateQA({ items: [...items, { id: generateId(), question: '', answerType: 'text' }] });
    };

    const updateQuestion = (index: number, updates: Partial<QAItem>) => {
      const newItems = [...items];
      newItems[index] = { ...newItems[index], ...updates };
      updateQA({ items: newItems });
    };

    const removeQuestion = (index: number) => {
      updateQA({ items: items.filter((_, i) => i !== index) });
    };

    return (
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Title</label>
          <input
            type="text"
            value={qaData.title || ''}
            onChange={e => updateQA({ title: e.target.value })}
            placeholder="Q&A Section Title"
            className="w-full px-2 py-1.5 text-sm border rounded"
          />
        </div>
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-xs font-medium text-neutral-600">Questions</label>
            <button onClick={addQuestion} className="text-xs text-purple-600 hover:text-purple-800">
              + Add Question
            </button>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {items.map((item, index) => (
              <div key={item.id} className="flex gap-2 items-start bg-neutral-50 p-2 rounded">
                <div className="flex-1 space-y-1">
                  <input
                    type="text"
                    value={item.question}
                    onChange={e => updateQuestion(index, { question: e.target.value })}
                    placeholder={`Question ${index + 1}`}
                    className="w-full px-2 py-1 text-xs border rounded"
                  />
                  <input
                    type="text"
                    value={item.description || ''}
                    onChange={e => updateQuestion(index, { description: e.target.value })}
                    placeholder="Description (optional)"
                    className="w-full px-2 py-1 text-xs border rounded"
                  />
                </div>
                <button
                  onClick={() => removeQuestion(index)}
                  className="p-1 text-neutral-400 hover:text-danger-500"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
            {items.length === 0 && (
              <p className="text-xs text-neutral-400 text-center py-2">No questions added</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Conviction config panel
  const renderConvictionConfigPanel = (widget: DashboardWidgetInstance) => {
    const dataKey = getWidgetDataKey(widget);
    const convData = (dashboardData[dataKey] as ConvictionDataContent) || { title: '', categories: [] };
    const categories_list: ConvictionCategory[] = convData.categories || [];

    const updateConviction = (updates: Partial<ConvictionDataContent>) => {
      handleDashboardDataChange(dataKey, { ...convData, ...updates } as ConvictionDataContent);
    };

    const addCategory = () => {
      updateConviction({
        categories: [...categories_list, { id: generateId(), name: '', minScore: 1, maxScore: 10, weight: 1 }],
      });
    };

    const updateCategory = (index: number, updates: Partial<ConvictionCategory>) => {
      const newCategories = [...categories_list];
      newCategories[index] = { ...newCategories[index], ...updates };
      updateConviction({ categories: newCategories });
    };

    const removeCategory = (index: number) => {
      updateConviction({ categories: categories_list.filter((_, i) => i !== index) });
    };

    return (
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Title</label>
          <input
            type="text"
            value={convData.title || ''}
            onChange={e => updateConviction({ title: e.target.value })}
            placeholder="Conviction Score Title"
            className="w-full px-2 py-1.5 text-sm border rounded"
          />
        </div>
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-xs font-medium text-neutral-600">Tags</label>
            <button onClick={addCategory} className="text-xs text-purple-600 hover:text-purple-800">
              Add Tag
            </button>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {categories_list.map((cat, index) => (
              <div key={cat.id} className="flex gap-2 items-start bg-neutral-50 p-2 rounded">
                <div className="flex-1">
                  <input
                    type="text"
                    value={cat.name}
                    onChange={e => updateCategory(index, { name: e.target.value })}
                    placeholder={`Tag ${index + 1}`}
                    className="w-full px-2 py-1 text-xs border rounded"
                  />
                </div>
                <button
                  onClick={() => removeCategory(index)}
                  className="text-danger-500 hover:text-danger-700 text-sm"
                >
                  ×
                </button>
              </div>
            ))}
            {categories_list.length === 0 && (
              <p className="text-xs text-neutral-400 text-center py-2">No tags added</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // OneDrive/Excel config panel
  const renderExcelConfigPanel = (widget: DashboardWidgetInstance) => {
    const overrides = widget.configOverrides || {};

    return (
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">File Name</label>
          <input
            type="text"
            value={(overrides.fileName as string) || ''}
            onChange={e => updateWidgetConfigOverrides(widget.id, { fileName: e.target.value })}
            placeholder="e.g., Financial Model.xlsx"
            className="w-full px-2 py-1.5 text-sm border rounded"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">OneDrive File ID</label>
          <input
            type="text"
            value={(overrides.fileId as string) || ''}
            onChange={e => updateWidgetConfigOverrides(widget.id, { fileId: e.target.value })}
            placeholder="OneDrive file ID..."
            className="w-full px-2 py-1.5 text-sm border rounded"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Or Direct URL</label>
          <input
            type="text"
            value={(overrides.fileUrl as string) || ''}
            onChange={e => updateWidgetConfigOverrides(widget.id, { fileUrl: e.target.value })}
            placeholder="https://..."
            className="w-full px-2 py-1.5 text-sm border rounded"
          />
        </div>
        <p className="text-xs text-neutral-500">
          Optional — if left empty, users will pick the file when they start the task.
        </p>
      </div>
    );
  };

  // Data-provider params config panel
  const renderDataProviderParamsPanel = (widget: DashboardWidgetInstance) => {
    const template = availableTemplates.find(t => t.id === widget.templateId);
    if (!template) return null;
    const def = widgetRegistry.get(template.type);
    const params = def?.dataProvider?.params;
    if (!params || params.length === 0) return null;

    const overrides = widget.configOverrides || {};

    return (
      <div className="space-y-3">
        {params.map(param => {
          const currentVal = (overrides as any)?.[param.key] ?? '';
          const displayDefault = param.defaultValue !== '' ? String(param.defaultValue) : '';
          return (
            <div key={param.key}>
              <label className="block text-xs font-medium text-neutral-600 mb-1">
                {param.label}
                {param.unit && (
                  <span className="font-normal text-neutral-400 ml-1">({param.unit})</span>
                )}
              </label>
              {param.type === 'select' && param.options ? (
                <select
                  value={currentVal || String(param.defaultValue)}
                  onChange={e => {
                    updateWidgetConfigOverrides(widget.id, { [param.key]: e.target.value });
                  }}
                  className="w-full border border-neutral-300 rounded px-2 py-1.5 text-sm bg-surface"
                >
                  {param.options.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={param.type || 'text'}
                  value={currentVal}
                  onChange={e => {
                    const val = param.type === 'number' && e.target.value
                      ? Number(e.target.value)
                      : e.target.value || undefined;
                    updateWidgetConfigOverrides(widget.id, { [param.key]: val });
                  }}
                  placeholder={param.placeholder || displayDefault || 'Default'}
                  className="w-full border border-neutral-300 rounded px-2 py-1.5 text-sm"
                />
              )}
              {displayDefault && !currentVal && (
                <p className="text-[10px] text-neutral-400 mt-0.5">
                  Default: {displayDefault}{param.unit ? ` ${param.unit}` : ''}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Scorecard layout selector — picks which presentation variant the
  // widget renders. Same data, different look.
  const renderScorecardConfigPanel = (widget: DashboardWidgetInstance) => {
    const template = availableTemplates.find(t => t.id === widget.templateId);
    if (!template) return null;

    const overrides = widget.configOverrides || {};
    const currentLayout =
      ((overrides.layout as ScorecardLayout | undefined) ??
        (template.defaultConfig as { layout?: ScorecardLayout } | undefined)?.layout ??
        'carousel') as ScorecardLayout;

    return (
      <div>
        <label className="block text-xs font-medium text-neutral-600 mb-1">
          Layout
        </label>
        <div className="grid grid-cols-2 gap-2">
          {SCORECARD_LAYOUTS.map(opt => {
            const isActive = currentLayout === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateWidgetConfigOverrides(widget.id, { layout: opt.value })}
                className={`text-center px-2.5 py-2 rounded border text-xs font-medium transition-colors ${
                  isActive
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-neutral-300 hover:border-neutral-400 bg-surface text-neutral-700'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Render the config panel for a widget based on its type
  const renderWidgetConfigPanel = (widget: DashboardWidgetInstance) => {
    const template = availableTemplates.find(t => t.id === widget.templateId);
    if (!template) return null;

    return (
      <div className="px-3 py-2 bg-surface border-t">
        {template.type === 'qa' && renderQAConfigPanel(widget)}
        {template.type === 'conviction' && renderConvictionConfigPanel(widget)}
        {template.type === 'onedrive' && renderExcelConfigPanel(widget)}
        {template.type === 'scorecard' && renderScorecardConfigPanel(widget)}
        {renderDataProviderParamsPanel(widget)}
      </div>
    );
  };

  // Render widget content by ID (used by DashboardGrid)
  const renderWidget = useCallback(
    (widgetId: string) => {
      const widgetInstance = widgets.find(w => w.id === widgetId);

      if (!widgetInstance) {
        return (
          <div className="h-full flex items-center justify-center text-neutral-400">
            Widget not found
          </div>
        );
      }

      const template = availableTemplates.find(t => t.id === widgetInstance.templateId);

      if (!template) {
        return (
          <div className="h-full flex items-center justify-center text-neutral-400">
            Template not found
          </div>
        );
      }

      // Check if this widget is a reference to another dashboard's widget
      const isDashboardReference = widgetInstance.dataSourceConfig?.type === 'dashboard_reference';

      if (isDashboardReference) {
        // Show the source widget's preview with a purple left border to indicate it's a reference
        const sourceWidgetType = widgetInstance.dataSourceConfig?.sourceWidgetType;
        const sourceDef = sourceWidgetType ? widgetRegistry.get(sourceWidgetType as any) : undefined;
        const SourcePreview = sourceDef?.Preview;

        return (
          <div className="h-full">
            {SourcePreview ? (
              <SourcePreview />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-neutral-400">
                {sourceWidgetType ? getWidgetLabel(sourceWidgetType) : 'Widget'} Reference
              </div>
            )}
          </div>
        );
      }

      const definition = widgetRegistry.get(template.type);
      const mergedConfig = {
        ...template.defaultConfig,
        ...(widgetInstance.configOverrides || {}),
      };

      // Configurable widgets always render live so the user sees their
      // configuration reflected in real time within the builder.
      if (hasConfigUI(template.type)) {
        return (
          <div className="h-full overflow-auto">
            <WidgetRenderer
              config={{
                type: template.type,
                title: widgetInstance.title || template.name,
                config: mergedConfig as any,
              }}
              dashboardData={dashboardData}
              onDashboardDataChange={handleDashboardDataChange}
            />
          </div>
        );
      }

      // Non-configurable widgets show the static preview illustration
      const PreviewComponent = definition?.Preview;

      if (PreviewComponent) {
        return (
          <div className="h-full overflow-auto">
            <PreviewComponent />
          </div>
        );
      }

      // Fallback: live renderer for widgets without a Preview component
      return (
        <div className="h-full overflow-auto">
          <WidgetRenderer
            config={{
              type: template.type,
              title: widgetInstance.title || template.name,
              config: mergedConfig as any,
            }}
            dashboardData={dashboardData}
            onDashboardDataChange={handleDashboardDataChange}
          />
        </div>
      );
    },
    [widgets, availableTemplates, dashboardData, handleDashboardDataChange],
  );

  // Helper: get dataKey for a widget instance
  const getWidgetDataKey = (widget: DashboardWidgetInstance): string => {
    const dk = widget.configOverrides?.dataKey;
    return typeof dk === 'string' && dk ? dk : widget.id;
  };

  // Helper: check if a widget type has a config panel in the dashboard builder
  const hasConfigUI = (type: WidgetType): boolean => {
    if (type === 'qa' || type === 'conviction' || type === 'onedrive' || type === 'scorecard' || type === 'trading_score') return true;
    const def = widgetRegistry.get(type);
    return !!(def?.dataProvider?.params && def.dataProvider.params.length > 0);
  };

  // Check if a configurable widget has been properly configured
  const isWidgetConfigured = (widget: DashboardWidgetInstance): boolean => {
    const template = availableTemplates.find(t => t.id === widget.templateId);
    if (!template) return true;
    if (!hasConfigUI(template.type)) return true;

    if (template.type === 'qa') {
      const dataKey = getWidgetDataKey(widget);
      const qaData = (dashboardData[dataKey] as QADataContent) || { items: [] };
      const items: QAItem[] = qaData.items || [];
      return items.length > 0 && items.some(item => item.question.trim() !== '');
    }

    if (template.type === 'conviction') {
      const dataKey = getWidgetDataKey(widget);
      const convData = (dashboardData[dataKey] as ConvictionDataContent) || { categories: [] };
      const cats: ConvictionCategory[] = convData.categories || [];
      return cats.length > 0 && cats.some(c => c.name.trim() !== '');
    }

    if (template.type === 'onedrive') {
      // Excel file selection is optional at dashboard level — users can pick the file
      // at task execution time when they know which asset they're working with
      return true;
    }

    // Data-provider params: configured if no params or all have defaults/values
    return true;
  };

  // Helper: update a widget's configOverrides
  const updateWidgetConfigOverrides = (widgetId: string, updates: Record<string, unknown>) => {
    setWidgets(prev =>
      prev.map(w =>
        w.id === widgetId
          ? { ...w, configOverrides: { ...(w.configOverrides || {}), ...updates } }
          : w,
      ),
    );
  };

  // Add widget to dashboard
  const addWidget = (template: WidgetTemplate) => {
    // Generate a stable dataKey for all configurable widgets
    // This ensures data can be stored consistently in dashboardData
    const configOverrides: Record<string, unknown> = {};
    if (SHAREABLE_WIDGET_TYPES.has(template.type) || isInputWidget(template.type) || hasConfigUI(template.type)) {
      configOverrides.dataKey = crypto.randomUUID();
    }

    addWidgetToLayout(template, template.name, configOverrides);
  };

  // Actually add the widget to the layout
  const addWidgetToLayout = (
    template: WidgetTemplate,
    title: string,
    configOverrides: Record<string, unknown>,
    dataSourceConfig?: DashboardWidgetInstance['dataSourceConfig'],
  ) => {
    const widgetId = `widget-${Date.now()}`;
    const isDashboardReference = dataSourceConfig?.type === 'dashboard_reference';
    const autoSummary = !isDashboardReference && SUMMARY_ELIGIBLE_TYPES.has(template.type);
    const newWidget: DashboardWidgetInstance = {
      id: widgetId,
      templateId: template.id,
      title,
      configOverrides,
      dataSourceConfig,
      ...(autoSummary ? { isSummary: true } : {}),
    };

    // Calculate position: place below existing items
    const maxY = getNextY(gridLayoutConfig.layout);
    const widgetDef = widgetRegistry.get(template.type);
    const size = widgetDef?.defaultSize;
    const newItem: GridItemPosition = {
      i: widgetId,
      x: 0,
      y: maxY,
      w: size?.w ?? DEFAULT_WIDGET_W,
      h: size?.h ?? DEFAULT_WIDGET_H,
      minW: size?.minW ?? DEFAULT_MIN_W,
      minH: size?.minH ?? DEFAULT_MIN_H,
    };

    setGridLayoutConfig(prev => ({
      ...prev,
      layout: [...prev.layout, newItem],
    }));

    setWidgets(prev => [...prev, newWidget]);
    setShowWidgetPicker(false);
    setSelectedCategory(null);
    setPickerSearch('');
  };

  // Add a shared widget (reference to widget from another dashboard)
  const addSharedWidget = (sharedWidget: SharedWidgetInfo) => {
    // Find the template for this widget type
    const template = availableTemplates.find(t => t.type === sharedWidget.widgetType);
    if (!template) {
      console.error('Could not find template for shared widget type:', sharedWidget.widgetType);
      return;
    }

    const title = `${sharedWidget.widgetTitle} (from ${sharedWidget.dashboardName})`;

    addWidgetToLayout(template, title, {}, {
      type: 'dashboard_reference',
      sourceDashboardId: sharedWidget.dashboardId,
      sourceDataKey: sharedWidget.dataKey,
      sourceWidgetType: sharedWidget.widgetType,
      readOnly: false,
    });
  };

  // Clear all widgets from dashboard
  const clearDashboard = () => {
    if (widgets.length === 0) return;
    setShowClearConfirm(true);
  };

  const confirmClearDashboard = () => {
    setWidgets([]);
    setGridLayoutConfig(getDefaultGridLayout());
  };

  // Remove a single widget
  const removeWidget = (widgetId: string) => {
    setGridLayoutConfig(prev => ({
      ...prev,
      layout: prev.layout.filter(item => item.i !== widgetId),
      tabGroups: prev.tabGroups
        .map(g => ({
          ...g,
          widgetIds: g.widgetIds.filter(id => id !== widgetId),
        }))
        .filter(g => g.widgetIds.length > 0),
    }));
    setWidgets(prev => prev.filter(w => w.id !== widgetId));
  };

  // Focus on a widget in the layout
  const focusWidget = (widgetId: string) => {
    // If widget is in a tab group, activate its tab
    setGridLayoutConfig(prev => ({
      ...prev,
      tabGroups: prev.tabGroups.map(g =>
        g.widgetIds.includes(widgetId) ? { ...g, activeWidgetId: widgetId } : g,
      ),
    }));
    // Scroll the widget's grid item into view
    const el = document.querySelector(`[data-grid-item="${widgetId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Rename a widget
  const renameWidget = (widgetId: string, newName: string) => {
    if (!newName.trim()) return;

    // Update the widget title in state
    setWidgets(prev => prev.map(w => (w.id === widgetId ? { ...w, title: newName.trim() } : w)));

    setEditingWidgetId(null);
    setEditingWidgetName('');
  };

  // Toggle widget sharing (for Q&A and Conviction widgets)
  const toggleWidgetSharing = (widgetId: string) => {
    setWidgets(prev => prev.map(w => (w.id === widgetId ? { ...w, isShared: !w.isShared } : w)));
  };

  // Toggle summary widget
  const toggleWidgetSummary = (widgetId: string) => {
    setWidgets(prev => prev.map(w =>
      w.id === widgetId ? { ...w, isSummary: !w.isSummary } : w
    ));
  };

  // Duplicate an existing widget
  const duplicateWidget = (widgetId: string) => {
    const source = widgets.find(w => w.id === widgetId);
    if (!source) return;

    const newId = `widget-${Date.now()}`;
    const newWidget: DashboardWidgetInstance = {
      ...source,
      id: newId,
      title: `${source.title} (Copy)`,
      isShared: false,
      isSummary: false,
      // Shareable widgets need their own unique dataKey
      configOverrides: {
        ...source.configOverrides,
        ...(source.configOverrides?.dataKey ? { dataKey: crypto.randomUUID() } : {}),
      },
    };

    // Find original's grid position and place copy offset by 1 cell
    const sourceLayout = gridLayoutConfig.layout.find(l => l.i === widgetId);
    const newItem: GridItemPosition = sourceLayout
      ? { ...sourceLayout, i: newId, x: sourceLayout.x + 1, y: sourceLayout.y + 1 }
      : { i: newId, x: 0, y: getNextY(gridLayoutConfig.layout), w: DEFAULT_WIDGET_W, h: DEFAULT_WIDGET_H, minW: DEFAULT_MIN_W, minH: DEFAULT_MIN_H };

    setWidgets(prev => [...prev, newWidget]);
    setGridLayoutConfig(prev => ({
      ...prev,
      layout: [...prev.layout, newItem],
    }));
  };

  // Handle grid layout changes (drag/resize)
  const handleGridLayoutChange = useCallback((newConfig: GridLayoutConfig) => {
    setGridLayoutConfig(newConfig);
  }, []);

  // Create a new tab group
  const createTabGroup = () => {
    const groupId = `tabgroup-${Date.now()}`;
    const maxY = getNextY(gridLayoutConfig.layout);
    const newGroup: TabGroupConfig = {
      id: groupId,
      widgetIds: [],
    };
    const newItem: GridItemPosition = {
      i: groupId,
      x: 0,
      y: maxY,
      w: DEFAULT_WIDGET_W,
      h: DEFAULT_WIDGET_H,
      minW: DEFAULT_TAB_GROUP_MIN_W,
      minH: DEFAULT_TAB_GROUP_MIN_H,
    };
    setGridLayoutConfig(prev => ({
      ...prev,
      layout: [...prev.layout, newItem],
      tabGroups: [...prev.tabGroups, newGroup],
    }));
  };

  // Move a standalone widget into a tab group
  const moveWidgetToTabGroup = (widgetId: string, tabGroupId: string) => {
    setGridLayoutConfig(prev => ({
      ...prev,
      // Remove standalone grid item for this widget
      layout: prev.layout.filter(item => item.i !== widgetId),
      tabGroups: prev.tabGroups.map(g =>
        g.id === tabGroupId && !g.widgetIds.includes(widgetId)
          ? { ...g, widgetIds: [...g.widgetIds, widgetId] }
          : g,
      ),
    }));
  };

  // Remove a widget from a tab group (make it standalone)
  const removeWidgetFromTabGroup = (tabGroupId: string, widgetId: string) => {
    const maxY = getNextY(gridLayoutConfig.layout);
    setGridLayoutConfig(prev => ({
      ...prev,
      layout: [
        ...prev.layout,
        {
          i: widgetId,
          x: 0,
          y: maxY,
          w: DEFAULT_WIDGET_W,
          h: DEFAULT_WIDGET_H,
          minW: DEFAULT_MIN_W,
          minH: DEFAULT_MIN_H,
        },
      ],
      tabGroups: prev.tabGroups
        .map(g =>
          g.id === tabGroupId
            ? { ...g, widgetIds: g.widgetIds.filter(id => id !== widgetId) }
            : g,
        )
        .filter(g => g.widgetIds.length > 0),
    }));
  };

  // Remove an entire tab group (widgets become standalone)
  const removeTabGroup = (tabGroupId: string) => {
    const group = gridLayoutConfig.tabGroups.find(g => g.id === tabGroupId);
    if (!group) return;

    const maxY = getNextY(gridLayoutConfig.layout);
    const standaloneItems: GridItemPosition[] = group.widgetIds.map((widgetId, index) => ({
      i: widgetId,
      x: (index % 2) * 6,
      y: maxY + Math.floor(index / 2) * DEFAULT_WIDGET_H,
      w: DEFAULT_WIDGET_W,
      h: DEFAULT_WIDGET_H,
      minW: DEFAULT_MIN_W,
      minH: DEFAULT_MIN_H,
    }));

    setGridLayoutConfig(prev => ({
      ...prev,
      layout: [...prev.layout.filter(item => item.i !== tabGroupId), ...standaloneItems],
      tabGroups: prev.tabGroups.filter(g => g.id !== tabGroupId),
    }));
  };

  // Handle active tab change within a tab group
  const handleActiveTabChange = useCallback((tabGroupId: string, widgetId: string) => {
    setGridLayoutConfig(prev => ({
      ...prev,
      tabGroups: prev.tabGroups.map(g =>
        g.id === tabGroupId ? { ...g, activeWidgetId: widgetId } : g,
      ),
    }));
  }, []);

  // Get widget title helper
  const getWidgetTitle = useCallback(
    (widgetId: string): string => {
      const widget = widgets.find(w => w.id === widgetId);
      if (!widget) return 'Widget';
      const template = availableTemplates.find(t => t.id === widget.templateId);
      return widget.title || template?.name || 'Widget';
    },
    [widgets, availableTemplates],
  );

  // Check if a widget requires user input
  const getWidgetIsInput = useCallback(
    (widgetId: string): boolean => {
      const widget = widgets.find(w => w.id === widgetId);
      if (!widget) return false;
      const template = availableTemplates.find(t => t.id === widget.templateId);
      return template ? isInputWidget(template.type) : false;
    },
    [widgets, availableTemplates],
  );

  // Check if a widget is in a tab group
  const getWidgetTabGroup = useCallback(
    (widgetId: string): TabGroupConfig | undefined => {
      return gridLayoutConfig.tabGroups.find(g => g.widgetIds.includes(widgetId));
    },
    [gridLayoutConfig.tabGroups],
  );

  // Handle tag changes — save to backend immediately if editing existing dashboard
  const handleTagsChange = async (newTagIds: string[]) => {
    setTagIds(newTagIds);

    if (dashboardId) {
      try {
        await dashboardService.updateDashboard(dashboardId, { tagIds: newTagIds });
      } catch (err) {
        console.error('Failed to update tags:', err);
      }
    }
  };

  const handleSaveClick = async () => {
    if (!name.trim()) {
      setError('Please enter a dashboard name');
      return;
    }
    if (!description.trim()) {
      setError('Please enter a dashboard description');
      return;
    }
    if (tagIds.length === 0) {
      setError('Please add at least one tag');
      return;
    }

    // Check all configurable widgets are properly configured
    const unconfiguredWidgets = widgets.filter(w => {
      const tmpl = availableTemplates.find(t => t.id === w.templateId);
      if (!tmpl) return false;
      const isDashRef = w.dataSourceConfig?.type === 'dashboard_reference';
      return hasConfigUI(tmpl.type) && !isDashRef && !isWidgetConfigured(w);
    });
    if (unconfiguredWidgets.length > 0) {
      const names = unconfiguredWidgets.map(w => w.title || 'Untitled').join(', ');
      setError(`These widgets need to be configured: ${names}`);
      return;
    }

    // If editing existing dashboard with active assignments, show version modal
    if (dashboardId && activeAssignmentsCount > 0) {
      setShowVersionModal(true);
      return;
    }

    // Otherwise, save directly
    await saveDashboard(false);
  };

  // Save dashboard with optional version creation for workflows
  const saveDashboard = async (createNewVersions: boolean, versionNotes?: string) => {
    try {
      setSaving(true);
      setError(null);

      const layoutConfig = gridLayoutConfig as unknown as Record<string, unknown>;

      if (dashboardId) {
        await dashboardService.updateDashboard(dashboardId, {
          name,
          description: description || undefined,
          layoutConfig,
          widgets,
          data: dashboardData,
          tagIds,
          createNewVersions,
          versionNotes,
        });

        // Reload active assignments count and version info after save
        try {
          const [assignmentsResponse, versionsResponse] = await Promise.all([
            dashboardService.getActiveAssignmentsCount(dashboardId),
            dashboardService.getDashboardVersions(dashboardId),
          ]);
          setActiveAssignmentsCount(assignmentsResponse.activeAssignmentsCount);
          setWorkflowsUsingDashboard(assignmentsResponse.workflowsUsingDashboard);
          setVersions(versionsResponse.versions);
          setCurrentVersion(versionsResponse.currentVersion);
        } catch (e) {
          console.error('Failed to reload active assignments/versions:', e);
        }
      } else {
        const newDashboard = await dashboardService.createDashboard({
          name,
          description: description || undefined,
          layoutConfig,
          widgets,
          data: dashboardData,
          tagIds,
        });
        navigate(`/dashboard-builder/${newDashboard.id}`, { replace: true });
      }
      setShowVersionModal(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save dashboard');
    } finally {
      setSaving(false);
    }
  };

  // Delete dashboard
  const handleDeleteDashboard = async () => {
    if (!dashboardId) return;

    try {
      setIsDeleting(true);
      await dashboardService.deleteDashboard(dashboardId);
      setShowDeleteModal(false);
      navigate('/dashboards');
    } catch (err: any) {
      console.error('Failed to delete dashboard:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete dashboard');
    } finally {
      setIsDeleting(false);
    }
  };

  // Copy dashboard with new name
  const handleCopyDashboard = async (newName: string) => {
    if (!dashboardId) return;

    try {
      setIsCopying(true);
      const newDashboard = await dashboardService.duplicateDashboard(dashboardId, newName);
      setShowCopyModal(false);
      navigate(`/dashboard-builder/${newDashboard.id}`);
    } catch (err: any) {
      console.error('Failed to copy dashboard:', err);
      toast.error(err.response?.data?.detail || 'Failed to copy dashboard');
    } finally {
      setIsCopying(false);
    }
  };

  // Export dashboard
  const handleExportDashboard = async (includeBundle: boolean = false) => {
    if (!dashboardId) return;

    try {
      setIsExporting(true);
      setShowExportMenu(false);
      if (includeBundle) {
        await exportImportService.exportDashboardBundle(dashboardId);
      } else {
        await exportImportService.exportDashboard(dashboardId);
      }
    } catch (err: any) {
      console.error('Failed to export dashboard:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to export dashboard');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSetupComplete = useCallback((result: SetupWizardResult) => {
    setName(result.name);
    setDescription(result.description);
    setTagIds(result.tagIds);
    setShowSetupWizard(false);
  }, []);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className={`h-full flex flex-col overflow-hidden ${isFullscreen ? 'fixed inset-0 z-40 bg-surface' : ''}`}>
      {/* Setup wizard for new dashboards */}
      {showSetupWizard && (
        <SetupWizardModal
          type="dashboard"
          onComplete={handleSetupComplete}
          onCancel={() => navigate('/dashboards')}
        />
      )}

      {/* Floating Toolbar (fullscreen mode) — draggable */}
      {isFullscreen && (
        <div
          className="absolute top-4 right-4 z-50"
          style={{ transform: `translate(${toolbarPos.x}px, ${toolbarPos.y}px)` }}
        >
          <div className="flex items-center gap-2 bg-surface/95 backdrop-blur-sm rounded-lg shadow-lg border px-4 py-2">
            <div
              onMouseDown={handleToolbarDragStart}
              className="cursor-grab active:cursor-grabbing p-1 -ml-2 mr-1 text-neutral-300 hover:text-neutral-500"
              title="Drag to move"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/>
                <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
                <circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/>
              </svg>
            </div>
            <span className="text-sm font-medium text-neutral-700 mr-2 max-w-[200px] truncate">{name}</span>
            <button
              onClick={() => setShowFullscreenWidgetPanel(prev => !prev)}
              className={`px-3 py-1.5 text-sm rounded flex items-center gap-1 transition-colors ${
                showFullscreenWidgetPanel
                  ? 'bg-neutral-200 text-neutral-800'
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              Widgets ({widgets.length})
            </button>
            <button
              onClick={() => setShowWidgetPicker(true)}
              className="px-3 py-1.5 text-sm bg-primary-50 text-primary-600 rounded hover:bg-primary-100 transition-colors"
            >
              + Add Widget
            </button>
            <button
              onClick={handleSaveClick}
              disabled={saving || hasOverlaps}
              title={hasOverlaps ? 'Fix overlapping widgets before saving' : undefined}
              className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 font-medium"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => setIsFullscreen(false)}
              className="px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-100 rounded flex items-center gap-1"
              title="Exit fullscreen (Esc)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
              </svg>
              Exit
            </button>
          </div>

          {/* Widget management panel (fullscreen) */}
          {showFullscreenWidgetPanel && (
            <div className="mt-2 bg-surface/95 backdrop-blur-sm rounded-lg shadow-lg border w-72 max-h-[70vh] flex flex-col ml-auto">
              <div className="px-3 py-2 border-b flex items-center justify-between">
                <span className="text-sm font-medium text-neutral-700">Widgets ({widgets.length})</span>
                {widgets.length > 0 && !viewingVersion && (
                  <button
                    onClick={clearDashboard}
                    className="text-xs text-neutral-400 hover:text-danger-500 transition-colors"
                  >
                    Clear All
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {widgets.length === 0 ? (
                  <p className="text-xs text-neutral-400 text-center py-4">No widgets added</p>
                ) : (
                  <div className="space-y-1">
                    {widgets.map(widget => {
                      const template = availableTemplates.find(t => t.id === widget.templateId);
                      const isDashboardReference = widget.dataSourceConfig?.type === 'dashboard_reference';
                      const isShared = widget.isShared || false;
                      return (
                        <div
                          key={widget.id}
                          className="group relative flex items-center gap-1 p-2 pl-3 rounded hover:bg-neutral-50 cursor-pointer"
                          onClick={() => focusWidget(widget.id)}
                        >
                          {(widget.isSummary || (isShared && !isDashboardReference) || isDashboardReference) && (
                            <div
                              className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full"
                              style={{
                                background: isDashboardReference
                                  ? '#a855f7'
                                  : widget.isSummary && isShared
                                    ? 'linear-gradient(to bottom, #fbbf24, #34d399)'
                                    : widget.isSummary ? '#fbbf24' : '#34d399'
                              }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            {editingWidgetId === widget.id ? (
                              <input
                                type="text"
                                value={editingWidgetName}
                                onChange={e => setEditingWidgetName(e.target.value)}
                                onBlur={() => renameWidget(widget.id, editingWidgetName)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') renameWidget(widget.id, editingWidgetName);
                                  else if (e.key === 'Escape') { setEditingWidgetId(null); setEditingWidgetName(''); }
                                }}
                                onClick={e => e.stopPropagation()}
                                autoFocus
                                className="w-full text-xs font-medium text-neutral-700 border-b border-primary-500 bg-transparent px-0 py-0.5 outline-none"
                              />
                            ) : (
                              <p
                                className="text-xs font-medium text-neutral-700"
                                onDoubleClick={e => { e.stopPropagation(); setEditingWidgetId(widget.id); setEditingWidgetName(widget.title || ''); }}
                                title="Double-click to rename"
                              >
                                {widget.title}
                              </p>
                            )}
                            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                              <span className="text-[10px] text-neutral-400">{template?.type || 'unknown'}</span>
                              {isDashboardReference && <span className="text-[10px] px-1 bg-purple-100 text-purple-700 rounded">Ref</span>}
                              {getWidgetTabGroup(widget.id) && <span className="text-[10px] px-1 bg-purple-100 text-purple-700 rounded">Tab</span>}
                            </div>
                          </div>
                          {!viewingVersion && (
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                setWidgetMenuPos({ top: rect.bottom + 4, left: rect.right - 160 });
                                setWidgetMenuId(widgetMenuId === widget.id ? null : widget.id);
                              }}
                              className="shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100"
                            >
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Tab Groups */}
              {gridLayoutConfig.tabGroups.length > 0 && (
                <div className="border-t px-2 py-2">
                  <p className="text-[10px] font-medium text-neutral-500 mb-1 px-1">Tab Groups</p>
                  <div className="space-y-1">
                    {gridLayoutConfig.tabGroups.map(group => (
                      <div key={group.id} className="p-1.5 rounded bg-neutral-50 border border-neutral-200 text-[10px]">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="font-medium text-neutral-700">{group.widgetIds.length} widget{group.widgetIds.length !== 1 ? 's' : ''}</span>
                          {!viewingVersion && (
                            <button onClick={() => removeTabGroup(group.id)} className="text-neutral-400 hover:text-danger-500 p-0.5" title="Delete group">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          )}
                        </div>
                        <div className="space-y-0.5">
                          {group.widgetIds.map(wId => (
                            <div key={wId} className="flex items-center gap-1 text-neutral-500">
                              <span className="truncate">{getWidgetTitle(wId)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {!viewingVersion && (
                <div className="p-2 border-t space-y-1">
                  <button
                    onClick={() => setShowWidgetPicker(true)}
                    className="w-full px-3 py-1.5 text-xs bg-primary-50 text-primary-600 rounded hover:bg-primary-100 transition-colors"
                  >
                    + Add Widget
                  </button>
                  <button
                    onClick={createTabGroup}
                    className="w-full px-3 py-1.5 text-xs bg-purple-50 text-purple-600 rounded hover:bg-purple-100 transition-colors"
                  >
                    + Create Tab Group
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className={`bg-surface border-b ${isFullscreen ? 'hidden' : ''}`}>
        {/* Top row: Back + Name + Version + Actions */}
        <div className="px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <button
              onClick={() => navigate('/dashboards')}
              className="h-8 px-3 text-sm text-neutral-700 border border-neutral-300 rounded shadow-sm hover:bg-neutral-50 transition-colors flex-shrink-0"
            >
              ← Back
            </button>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={!!viewingVersion}
              className={`h-8 text-sm font-semibold border border-neutral-200 rounded px-2.5 outline-none min-w-0 ${viewingVersion ? 'text-neutral-500 bg-neutral-50' : 'bg-surface'}`}
              placeholder="Dashboard name"
            />
            {/* Version badge */}
            {dashboardId && (
              <div className="relative flex-shrink-0" ref={versionDropdownRef}>
                <button
                  onClick={() => setShowVersionDropdown(!showVersionDropdown)}
                  className={`h-8 flex items-center gap-1 px-2.5 rounded text-xs font-medium border ${
                    viewingVersion
                      ? 'bg-warning-50 text-warning-700 border-warning-200'
                      : 'bg-neutral-50 text-neutral-600 border-neutral-200 hover:bg-neutral-100'
                  }`}
                >
                  {viewingVersion ? (
                    <span>Viewing {viewingVersion.versionLabel} (read-only)</span>
                  ) : (
                    <span>v{currentVersion}</span>
                  )}
                  {versions.length > 0 && (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>
                {showVersionDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-surface rounded-lg shadow-lg border z-50">
                    <div className="p-2 border-b">
                      <p className="text-xs text-neutral-500 font-medium">VERSION HISTORY</p>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      <button
                        onClick={() => { if (viewingVersion) exitVersionView(); setShowVersionDropdown(false); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex justify-between items-center ${!viewingVersion ? 'bg-primary-50' : ''}`}
                      >
                        <span>v{currentVersion} (current)</span>
                        {!viewingVersion && <span className="text-xs text-primary-600">editing</span>}
                      </button>
                      {versions.map(version => (
                        <button
                          key={version.id}
                          onClick={() => loadVersionSnapshot(version.id)}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 ${viewingVersion?.id === version.id ? 'bg-warning-50' : ''}`}
                        >
                          <div className="flex justify-between items-center">
                            <span>{version.versionLabel}</span>
                            <span className="text-xs text-neutral-400">{new Date(version.createdAt).toLocaleDateString()}</span>
                          </div>
                          {version.notes && <p className="text-xs text-neutral-500 mt-0.5 truncate" title={version.notes}>{version.notes}</p>}
                        </button>
                      ))}
                      {versions.length === 0 && <p className="px-3 py-2 text-xs text-neutral-400">No previous versions</p>}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Active assignments — inline next to version */}
            {dashboardId && activeAssignmentsCount > 0 && !viewingVersion && (
              <span className="h-8 flex items-center text-xs font-medium text-warning-700 bg-warning-50 border border-warning-200 px-2.5 rounded flex-shrink-0">
                {activeAssignmentsCount} active workflow{activeAssignmentsCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {viewingVersion ? (
              <button onClick={exitVersionView} className="px-3 py-1.5 text-sm bg-warning-500 text-white rounded-lg hover:bg-warning-600">
                Exit Version View
              </button>
            ) : (
              <>
                <button
                  onClick={() => { setToolbarPos({ x: 0, y: 0 }); setIsFullscreen(true); }}
                  className="h-8 px-3 text-xs font-medium text-neutral-700 border border-neutral-300 rounded shadow-sm hover:bg-neutral-50 transition-colors flex items-center gap-1"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                  Fullscreen
                </button>
                <button
                  onClick={handleSaveClick}
                  disabled={saving || hasOverlaps}
                  title={hasOverlaps ? 'Fix overlapping widgets before saving' : undefined}
                  className="h-8 px-4 text-xs font-medium text-white bg-primary-600 rounded shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {hasOverlaps ? 'Overlapping Widgets' : saving ? 'Saving...' : dashboardId ? 'Save Changes' : 'Create Dashboard'}
                </button>
                {dashboardId && (
                  <>
                    <div className="w-px h-5 bg-neutral-200" />
                    <div className="relative">
                      <button
                        onClick={() => setShowExportMenu(!showExportMenu)}
                        disabled={isExporting}
                        className="h-8 px-2.5 text-xs font-medium text-neutral-700 border border-neutral-300 rounded shadow-sm hover:bg-neutral-50 transition-colors flex items-center gap-1"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {isExporting ? '...' : 'Export'}
                      </button>
                      {showExportMenu && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-surface rounded-lg shadow-lg border z-50">
                          <button onClick={() => handleExportDashboard(false)} className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-50 rounded-t-lg">Export Dashboard</button>
                          <button onClick={() => handleExportDashboard(true)} className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-50 rounded-b-lg border-t">Export with Widgets</button>
                        </div>
                      )}
                    </div>
                    <button onClick={() => setShowCopyModal(true)} disabled={isCopying} className="h-8 px-2.5 text-xs font-medium text-neutral-700 border border-neutral-300 rounded shadow-sm hover:bg-neutral-50 transition-colors">
                      Copy
                    </button>
                    <button onClick={() => setShowDeleteModal(true)} disabled={isDeleting} className="h-8 px-2.5 text-xs font-medium text-danger-600 border border-danger-300 rounded shadow-sm hover:bg-danger-50 transition-colors">
                      Delete
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Meta row: description + tags */}
        <div className="px-4 pb-2.5 flex items-center gap-3">
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            disabled={!!viewingVersion}
            className={`flex-1 text-sm border border-neutral-200 rounded-lg px-3 py-1.5 ${viewingVersion ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-50 focus:bg-surface focus:ring-1/20'} outline-none`}
            placeholder="Dashboard description"
          />
          {!viewingVersion && (
            <div className="flex-shrink-0 min-w-[200px] max-w-md">
              <TagPicker
                value={tagIds}
                onChange={handleTagsChange}
                tags={allTags}
                onTagsChanged={loadTags}
                placeholder="Add tag..."
              />
            </div>
          )}
        </div>
      </div>

      {/* Version View Banner */}
      {viewingVersion && !isFullscreen && (
        <div className="px-4 py-2 bg-warning-50 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-warning-600"><FontAwesomeIcon icon={faScroll} /></span>
            <span className="text-sm text-warning-700 font-medium">
              Viewing {viewingVersion.versionLabel}
            </span>
            <span className="text-xs text-warning-500">
              ({new Date(viewingVersion.createdAt).toLocaleString()})
            </span>
            {viewingVersion.notes && <span className="text-xs text-warning-600 italic">- {viewingVersion.notes}</span>}
          </div>
          <button onClick={exitVersionView} className="text-sm text-warning-700 hover:text-warning-900 underline">
            Return to current
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="px-4 py-2 bg-danger-50 text-danger-700 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-4 underline">Dismiss</button>
        </div>
      )}

      {/* Main Content Area with Sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Widget Sidebar */}
        <div
          className={`bg-neutral-50 border-r flex flex-col transition-all duration-200 ${
            isFullscreen ? 'hidden' : sidebarCollapsed ? 'w-10' : 'w-64'
          }`}
        >
          {/* Sidebar Header */}
          <div className="px-3 py-2 border-b bg-surface flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-neutral-700">
                  Widgets ({widgets.length})
                </span>
                {widgets.length > 0 && !viewingVersion && (
                  <button
                    onClick={clearDashboard}
                    className="text-xs text-neutral-400 hover:text-danger-500 transition-colors"
                    title="Clear all widgets"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1 hover:bg-neutral-100 rounded text-neutral-500"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? '»' : '«'}
            </button>
          </div>

          {/* Widget List */}
          {!sidebarCollapsed && (
            <div className="flex-1 overflow-y-auto p-2">
              {widgets.length === 0 ? (
                <p className="text-xs text-neutral-400 text-center py-4">No widgets added</p>
              ) : (
                <div className="space-y-1">
                  {widgets.map(widget => {
                    const template = availableTemplates.find(t => t.id === widget.templateId);
                    const isWorkflowSource = widget.dataSourceConfig?.source === 'workflow';
                    // Check if this widget is a reference to another dashboard's widget
                    const isDashboardReference =
                      widget.dataSourceConfig?.type === 'dashboard_reference';
                    const isShared = widget.isShared || false;
                    const isConfigExpanded = expandedConfigWidgetId === widget.id;
                    const widgetHasConfig = template && hasConfigUI(template.type) && !isDashboardReference;
                    return (
                      <div key={widget.id} className="rounded-lg overflow-hidden">
                        <div
                          className="group relative flex items-center gap-1 p-2 pl-3 hover:bg-surface cursor-pointer"
                          onClick={() => focusWidget(widget.id)}
                        >
                          {(widget.isSummary || (isShared && !isDashboardReference) || isDashboardReference) && (
                            <div
                              className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full"
                              style={{
                                background: isDashboardReference
                                  ? '#a855f7'
                                  : widget.isSummary && isShared
                                    ? 'linear-gradient(to bottom, #fbbf24, #34d399)'
                                    : widget.isSummary ? '#fbbf24' : '#34d399'
                              }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            {editingWidgetId === widget.id ? (
                              <input
                                type="text"
                                value={editingWidgetName}
                                onChange={e => setEditingWidgetName(e.target.value)}
                                onBlur={() => renameWidget(widget.id, editingWidgetName)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') renameWidget(widget.id, editingWidgetName);
                                  else if (e.key === 'Escape') { setEditingWidgetId(null); setEditingWidgetName(''); }
                                }}
                                onClick={e => e.stopPropagation()}
                                autoFocus
                                className="w-full text-sm font-medium text-neutral-700 border-b border-primary-500 bg-transparent px-0 py-0.5 outline-none focus:ring-0"
                              />
                            ) : (
                              <p
                                className="text-sm font-medium text-neutral-700"
                                onDoubleClick={e => {
                                  e.stopPropagation();
                                  if (!viewingVersion) {
                                    setEditingWidgetId(widget.id);
                                    setEditingWidgetName(widget.title || '');
                                  }
                                }}
                                title="Double-click to rename"
                              >
                                {widget.title}
                              </p>
                            )}
                            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                              <span className="text-xs text-neutral-400">{template?.type || 'unknown'}</span>
                              {isWorkflowSource && <span className="text-[10px] px-1 bg-purple-100 text-purple-700 rounded">Workflow</span>}
                              {isDashboardReference && <span className="text-[10px] px-1 bg-success-100 text-success-700 rounded">Ref</span>}
                              {widgetHasConfig && !isDashboardReference && (
                                isWidgetConfigured(widget)
                                  ? <span className="text-[10px] px-1 bg-success-100 text-success-700 rounded">Configured</span>
                                  : <span className="text-[10px] px-1 bg-warning-100 text-warning-700 rounded">Needs config</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            {widgetHasConfig && !viewingVersion && (
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  setExpandedConfigWidgetId(isConfigExpanded ? null : widget.id);
                                }}
                                className={`p-1 rounded text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 ${isConfigExpanded ? 'opacity-100 text-purple-600' : 'opacity-0 group-hover:opacity-100'}`}
                                title="Configure widget"
                              >
                                <svg className={`w-4 h-4 transition-transform ${isConfigExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            )}
                            {!viewingVersion && (
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setWidgetMenuPos({ top: rect.bottom + 4, left: rect.right - 160 });
                                  setWidgetMenuId(widgetMenuId === widget.id ? null : widget.id);
                                }}
                                className="p-1 rounded opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                        {isConfigExpanded && widgetHasConfig && renderWidgetConfigPanel(widget)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tab Groups Section in Sidebar */}
          {!sidebarCollapsed && gridLayoutConfig.tabGroups.length > 0 && (
            <div className="border-t px-2 py-2">
              <p className="text-xs font-medium text-neutral-500 mb-1 px-1">Tab Groups</p>
              <div className="space-y-1">
                {gridLayoutConfig.tabGroups.map(group => (
                  <div
                    key={group.id}
                    className="p-2 rounded bg-surface border border-neutral-200 text-xs"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-neutral-700">
                        {group.widgetIds.length} widget{group.widgetIds.length !== 1 ? 's' : ''}
                      </span>
                      {!viewingVersion && (
                        <button
                          onClick={() => removeTabGroup(group.id)}
                          className="text-neutral-400 hover:text-danger-500 p-0.5"
                          title="Delete group"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      {group.widgetIds.map(wId => (
                        <div key={wId} className="flex items-center gap-1 text-neutral-500">
                          <span className="truncate">{getWidgetTitle(wId)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Widget / Create Tab Group Buttons in Sidebar */}
          {!sidebarCollapsed && !viewingVersion && (
            <div className="p-2 border-t space-y-1">
              <button
                onClick={() => setShowWidgetPicker(true)}
                className="w-full px-3 py-2 text-sm bg-primary-50 text-primary-600 rounded hover:bg-primary-100 transition-colors"
              >
                + Add Widget
              </button>
              <button
                onClick={createTabGroup}
                className="w-full px-3 py-2 text-sm bg-purple-50 text-purple-600 rounded hover:bg-purple-100 transition-colors"
              >
                + Create Tab Group
              </button>
            </div>
          )}
        </div>

        {/* Grid Layout Area */}
        <div className="flex-1 h-full min-h-0 relative overflow-hidden">
          {widgets.length === 0 && gridLayoutConfig.tabGroups.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-neutral-500">
              <div className="text-6xl mb-4"><FontAwesomeIcon icon={faClipboard} /></div>
              <p className="text-lg mb-4">
                {viewingVersion ? 'This version has no widgets' : 'No widgets added yet'}
              </p>
              {!viewingVersion && (
                <button
                  onClick={() => setShowWidgetPicker(true)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  + Add Your First Widget
                </button>
              )}
            </div>
          ) : (
            <DashboardGrid
              layoutConfig={gridLayoutConfig}
              widgets={widgets}
              isEditable={!viewingVersion}
              onLayoutChange={handleGridLayoutChange}
              overlappingItemIds={overlappingItemIds}
              renderWidget={renderWidget}
              onActiveTabChange={handleActiveTabChange}
              getWidgetTitle={getWidgetTitle}
              getWidgetIsInput={getWidgetIsInput}
              getWidgetIsSummary={(wId) => {
                const w = widgets.find(ww => ww.id === wId);
                return w?.isSummary || false;
              }}
              getWidgetIsShared={(wId) => {
                const w = widgets.find(ww => ww.id === wId);
                return w?.isShared || false;
              }}
            />
          )}
        </div>
      </div>

      {/* Widget action menu (portalled) */}
      {widgetMenuId && (() => {
        const widget = widgets.find(w => w.id === widgetMenuId);
        if (!widget) return null;
        const template = availableTemplates.find(t => t.id === widget.templateId);
        const isDashboardReference = widget.dataSourceConfig?.type === 'dashboard_reference';
        const isShareable = template && SHAREABLE_WIDGET_TYPES.has(template.type) && !isDashboardReference;
        const isSummaryEligible = template && SUMMARY_ELIGIBLE_TYPES.has(template.type) && !isDashboardReference;
        return createPortal(
          <div
            ref={widgetMenuRef}
            className="fixed w-40 bg-surface border border-neutral-200 rounded-lg shadow-lg z-50 py-1 overflow-hidden"
            style={{ top: widgetMenuPos.top, left: widgetMenuPos.left }}
          >
            {isSummaryEligible && (
              <button
                onClick={() => { toggleWidgetSummary(widget.id); setWidgetMenuId(null); }}
                className="w-full px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50"
              >
                {widget.isSummary ? 'Unsummarize' : 'Summarize'}
              </button>
            )}
            {isShareable && (
              <button
                onClick={() => { toggleWidgetSharing(widget.id); setWidgetMenuId(null); }}
                className="w-full px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50"
              >
                {widget.isShared ? 'Unshare' : 'Share'}
              </button>
            )}
            <button
              onClick={() => { setEditingWidgetId(widget.id); setEditingWidgetName(widget.title || ''); setWidgetMenuId(null); }}
              className="w-full px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50"
            >
              Rename
            </button>
            <button
              onClick={() => { duplicateWidget(widget.id); setWidgetMenuId(null); }}
              className="w-full px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50"
            >
              Duplicate
            </button>
            {/* Tab group actions */}
            {(() => {
              const tabGroup = getWidgetTabGroup(widget.id);
              if (tabGroup) {
                return (
                  <button
                    onClick={() => { removeWidgetFromTabGroup(tabGroup.id, widget.id); setWidgetMenuId(null); }}
                    className="w-full px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                  >
                    Remove from Tab
                  </button>
                );
              }
              if (gridLayoutConfig.tabGroups.length > 0) {
                return gridLayoutConfig.tabGroups.map((g, idx) => (
                  <button
                    key={g.id}
                    onClick={() => { moveWidgetToTabGroup(widget.id, g.id); setWidgetMenuId(null); }}
                    className="w-full px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                  >
                    Move to Tab {idx + 1}
                  </button>
                ));
              }
              return null;
            })()}
            <div className="border-t border-neutral-100 my-1" />
            <button
              onClick={() => { removeWidget(widget.id); setWidgetMenuId(null); }}
              className="w-full px-3 py-1.5 text-left text-sm text-danger-600 hover:bg-danger-50"
            >
              Remove
            </button>
          </div>,
          document.body,
        );
      })()}

      {/* Widget Picker Modal */}
      {showWidgetPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => { setShowWidgetPicker(false); setPickerSearch(''); setSelectedCategory(null); }}
          />
          <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-3xl h-[80vh] overflow-hidden flex flex-col">
            {/* Header with search */}
            <div className="px-5 pt-5 pb-3">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-neutral-900">Add Widget</h2>
                <button
                  onClick={() => { setShowWidgetPicker(false); setPickerSearch(''); setSelectedCategory(null); }}
                  className="text-neutral-400 hover:text-neutral-600 p-1"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={pickerSearch}
                  onChange={e => setPickerSearch(e.target.value)}
                  placeholder="Search by name or description..."
                  className="w-full pl-9 pr-8 py-2 text-sm border border-neutral-200 rounded-lg bg-neutral-50"
                  autoFocus
                />
                {pickerSearch && (
                  <button onClick={() => setPickerSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 text-sm">
                    ×
                  </button>
                )}
              </div>
              {/* Filter + Shared */}
              <div className="flex items-center gap-2 mt-3">
                {/* Tag filter button with dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowTagFilter(prev => !prev)}
                    className={`h-8 px-2.5 flex items-center gap-1.5 text-xs rounded-lg border transition-colors ${
                      selectedCategory && selectedCategory !== 'shared'
                        ? 'bg-primary-50 border-primary-200 text-primary-700'
                        : 'bg-neutral-50 border-neutral-200 text-neutral-600 hover:bg-neutral-100'
                    }`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    {selectedCategory && selectedCategory !== 'shared'
                      ? CATEGORY_INFO[selectedCategory]?.label
                      : 'Filter'}
                  </button>
                  {showTagFilter && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowTagFilter(false)} />
                      <div className="absolute left-0 top-full mt-1 w-48 bg-surface border border-neutral-200 rounded-lg shadow-lg z-20 py-1 overflow-hidden">
                        {CATEGORY_ORDER.filter(c => c !== 'shared').map(category => {
                          const info = CATEGORY_INFO[category];
                          const count = templatesByCategory[category]?.length || 0;
                          const isActive = selectedCategory === category;
                          return (
                            <button
                              key={category}
                              onClick={() => {
                                setSelectedCategory(isActive ? null : category);
                                setShowTagFilter(false);
                              }}
                              className={`w-full px-3 py-2 text-left text-xs flex items-center justify-between transition-colors ${
                                isActive ? 'bg-primary-50 text-primary-700' : 'text-neutral-700 hover:bg-neutral-50'
                              }`}
                            >
                              <span>{info.label}</span>
                              <span className="text-neutral-400">{count}</span>
                            </button>
                          );
                        })}
                        {selectedCategory && selectedCategory !== 'shared' && (
                          <>
                            <div className="border-t border-neutral-100 my-1" />
                            <button
                              onClick={() => { setSelectedCategory(null); setShowTagFilter(false); }}
                              className="w-full px-3 py-2 text-left text-xs text-neutral-500 hover:bg-neutral-50"
                            >
                              Clear filter
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Shared button */}
                <button
                  onClick={() => { setSelectedCategory(selectedCategory === 'shared' ? null : 'shared'); if (selectedCategory !== 'shared') loadSharedWidgets(); }}
                  className={`h-8 px-2.5 text-xs font-medium rounded-lg border transition-colors ${
                    selectedCategory === 'shared'
                      ? 'bg-purple-50 border-purple-200 text-purple-700'
                      : 'bg-neutral-50 border-neutral-200 text-neutral-600 hover:bg-neutral-100'
                  }`}
                >
                  Shared
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-y-auto border-t">
              {selectedCategory !== 'shared' ? (
                filteredPickerTemplates.length > 0 ? (
                  <table className="w-full">
                    <thead className="sticky top-0 bg-neutral-50 z-10">
                      <tr className="text-[11px] text-neutral-400 uppercase tracking-wider border-b border-neutral-200">
                        <th className="py-2 px-5 text-left font-medium">Widget</th>
                        <th className="py-2 px-4 text-left font-medium">Tag</th>
                        <th className="py-2 px-4 text-left font-medium hidden md:table-cell">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPickerTemplates.map(template => {
                        const category = getWidgetCategory(template.type);
                        const categoryInfo = category ? CATEGORY_INFO[category] : null;
                        return (
                          <tr
                            key={template.id}
                            onClick={() => addWidget(template)}
                            className="border-b border-neutral-100 last:border-b-0 hover:bg-primary-50 transition-colors cursor-pointer group"
                          >
                            <td className="py-2.5 px-5">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-neutral-900 group-hover:text-primary-700 transition-colors truncate">
                                  {template.name}
                                </p>
                                <p className="text-[11px] text-neutral-400">{getWidgetLabel(template.type)}</p>
                              </div>
                            </td>
                            <td className="py-2.5 px-4">
                              {categoryInfo && (
                                <span className="text-xs text-neutral-500">{categoryInfo.label}</span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 hidden md:table-cell">
                              {template.description ? (
                                <span className="text-xs text-neutral-400 line-clamp-2">{template.description}</span>
                              ) : (
                                <span className="text-xs text-neutral-300">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-16">
                    <p className="text-sm text-neutral-500">
                      {pickerSearch ? `No widgets matching "${pickerSearch}"` : 'No widgets available'}
                    </p>
                    {pickerSearch && (
                      <button onClick={() => setPickerSearch('')} className="text-sm text-primary-600 hover:underline mt-2">Clear search</button>
                    )}
                  </div>
                )
              ) : (
                /* Shared Widgets */
                loadingSharedWidgets ? (
                  <div className="text-center py-16">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mb-3"></div>
                    <p className="text-sm text-neutral-500">Loading shared widgets...</p>
                  </div>
                ) : filteredSharedWidgets.length > 0 ? (
                  <table className="w-full">
                    <thead className="sticky top-0 bg-neutral-50 z-10">
                      <tr className="text-[11px] text-neutral-400 uppercase tracking-wider border-b border-neutral-200">
                        <th className="py-2 px-5 text-left font-medium">Widget</th>
                        <th className="py-2 px-4 text-left font-medium">Source</th>
                        <th className="py-2 px-4 text-left font-medium">Tag</th>
                        <th className="py-2 px-4 text-left font-medium hidden md:table-cell">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSharedWidgets.map(widget => {
                        const widgetCategory = getWidgetCategory(widget.widgetType);
                        const categoryInfo = widgetCategory ? CATEGORY_INFO[widgetCategory] : null;
                        return (
                          <tr
                            key={`${widget.dashboardId}-${widget.widgetId}`}
                            onClick={() => addSharedWidget(widget)}
                            className="border-b border-neutral-100 last:border-b-0 hover:bg-purple-50 transition-colors cursor-pointer group"
                          >
                            <td className="py-2.5 px-5">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-neutral-900 truncate">{widget.widgetTitle}</p>
                                <p className="text-[11px] text-neutral-400">{getWidgetLabel(widget.widgetType)}</p>
                              </div>
                            </td>
                            <td className="py-2.5 px-4">
                              <span className="text-xs text-neutral-600 truncate">{widget.dashboardName}</span>
                            </td>
                            <td className="py-2.5 px-4">
                              {categoryInfo && (
                                <span className="text-xs text-neutral-500">{categoryInfo.label}</span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 hidden md:table-cell">
                              <span className="text-xs text-neutral-400">
                                Shared {getWidgetLabel(widget.widgetType)} from {widget.dashboardName}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-16">
                    <FontAwesomeIcon icon={faLink} className="text-neutral-300 text-2xl mb-3" />
                    <p className="text-sm text-neutral-500 mb-1">
                      {pickerSearch ? `No shared widgets matching "${pickerSearch}"` : 'No shared widgets available'}
                    </p>
                    <p className="text-xs text-neutral-400">Mark widgets as shared in other dashboards to reference them here</p>
                  </div>
                )
              )}
            </div>

            {/* Footer with count */}
            <div className="px-5 py-2.5 border-t bg-neutral-50 text-xs text-neutral-400">
              {selectedCategory === 'shared'
                ? `${filteredSharedWidgets.length} shared widget${filteredSharedWidgets.length !== 1 ? 's' : ''}`
                : `${filteredPickerTemplates.length} widget${filteredPickerTemplates.length !== 1 ? 's' : ''} available`}
            </div>
          </div>
        </div>
      )}


      {/* Version Modal - shown when saving with active assignments */}
      {showVersionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowVersionModal(false)}
          />
          <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h2 className="text-xl font-semibold">Save Dashboard Changes</h2>
            </div>
            <div className="p-6">
              <div className="mb-4 p-3 bg-warning-50 border border-warning-200 rounded-lg">
                <p className="text-sm text-warning-800">
                  This dashboard is used by{' '}
                  <strong>
                    {workflowsUsingDashboard.length} workflow
                    {workflowsUsingDashboard.length !== 1 ? 's' : ''}
                  </strong>{' '}
                  with{' '}
                  <strong>
                    {activeAssignmentsCount} active assignment
                    {activeAssignmentsCount !== 1 ? 's' : ''}
                  </strong>
                  . How would you like to save your changes?
                </p>
                {workflowsUsingDashboard.length > 0 && (
                  <div className="mt-2 text-xs text-warning-700">
                    Workflows: {workflowsUsingDashboard.map(w => w.name).join(', ')}
                  </div>
                )}
              </div>

              {/* Version notes input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Version Notes (optional)
                </label>
                <textarea
                  id="version-notes-input"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none"
                  rows={2}
                  placeholder="Describe what changed in this version..."
                />
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    const notesInput = document.getElementById(
                      'version-notes-input',
                    ) as HTMLTextAreaElement;
                    saveDashboard(true, notesInput?.value || undefined);
                  }}
                  className="w-full p-4 border border-neutral-200 rounded-lg text-left hover:border-primary-500 hover:bg-primary-900/20 transition-colors"
                >
                  <p className="font-medium text-neutral-900">Create New Workflow Versions</p>
                  <p className="text-sm text-neutral-500 mt-1">
                    Creates new versions for affected workflows. Current assignments will
                    continue using the
                    <strong> previous dashboard layout</strong>. New assignments will use the
                    updated dashboard.
                  </p>
                </button>

                <button
                  onClick={() => saveDashboard(false)}
                  className="w-full p-4 border border-neutral-200 rounded-lg text-left hover:border-warning-500 hover:bg-warning-900/20 transition-colors"
                >
                  <p className="font-medium text-neutral-900">Update In-Place</p>
                  <p className="text-sm text-neutral-500 mt-1">
                    <strong>All active assignments</strong> will see the updated dashboard
                    immediately. No version history will be created for the workflows.
                  </p>
                </button>
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-neutral-50 flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowVersionModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteDashboard}
        itemName={name}
        itemType="dashboard"
        isDeleting={isDeleting}
      />

      {/* Copy Modal */}
      <CopyModal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        onConfirm={handleCopyDashboard}
        originalName={name}
        itemType="dashboard"
        isCopying={isCopying}
      />

      <ConfirmationModal
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onAction={confirmClearDashboard}
        title="Clear Dashboard"
        description="Are you sure you want to remove all widgets from this dashboard?"
        actionText="Remove All"
      />
    </div>
  );
};

export default DashboardBuilder;
